import { useEffect, useRef } from 'react';

import gt from 'semver/functions/gt';
import valid from 'semver/functions/valid';

export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export function versionJsonUrl(basePath: string): string {
    return `${basePath.replace(/\/+$/, '')}/version.json`;
}

interface UseNewVersionCheckOptions {
    currentVersion: string;
    basePath: string;
    onNewVersion: (version: string) => void;
    enabled?: boolean;
    intervalMs?: number;
}

/**
 * Polls the deployed version.json and calls onNewVersion on every check that
 * finds a version newer than the running bundle. Failures are ignored: the
 * next check tries again.
 */
export default function useNewVersionCheck({
    currentVersion,
    basePath,
    onNewVersion,
    enabled = true,
    intervalMs = VERSION_CHECK_INTERVAL_MS,
}: UseNewVersionCheckOptions): void {
    const onNewVersionRef = useRef(onNewVersion);
    onNewVersionRef.current = onNewVersion;

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        const url = versionJsonUrl(basePath);

        const check = async () => {
            try {
                const response = await fetch(url, { cache: 'no-store' });
                if (!response.ok) return;
                const body = (await response.json()) as { version?: unknown };
                const deployed = body?.version;
                if (
                    !cancelled &&
                    typeof deployed === 'string' &&
                    valid(deployed) &&
                    gt(deployed, currentVersion)
                ) {
                    onNewVersionRef.current(deployed);
                }
            } catch {
                // Offline, mid-deploy or not JSON: try again next time.
            }
        };

        const intervalId = window.setInterval(check, intervalMs);
        const handleFocus = () => {
            void check();
        };
        window.addEventListener('focus', handleFocus);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            window.removeEventListener('focus', handleFocus);
        };
    }, [enabled, basePath, currentVersion, intervalMs]);
}
